import { useDrawer } from '@/core/context/DrawerContext'
import { SettingsTeamModule } from '@/core/modules/team/team.module'
import { normalizeEmail } from '@/core/utils/email'
import { Analytics } from '@core/services/mixpanel'
import { ActionButton } from '@/ui/components/buttons/action'
import { generateToast } from '@/ui/components/toasting'
import { FormProvider, useForm } from 'react-hook-form'
import { Content, Footer, Header, Wrapper } from './inviteUser.styles'
import { InviteUserForm } from './inviteUserForm'
import Typography from '@/ui/components/typography'

const InviteUser = () => {
    const form = useForm<TInviteUserForm>({ mode: 'onBlur' })
    const { drawerState, setDrawerState } = useDrawer()
    const [loading, setLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    // Keyed on isDrawerOpen, not mount: reopening during the close animation
    // keeps this body mounted, and the provider has already reset onClose.
    useEffect(() => {
        if (!drawerState.isDrawerOpen) return

        setDrawerState((prevState) => ({
            ...prevState,
            onClose: () => {
                const { email } = form.getValues()

                Analytics.event('SEM::invitee_joined_ASM_canceled', {
                    inviteeEmail: email,
                    hadInput: Boolean(email),
                })
            },
        }))
    }, [drawerState.isDrawerOpen])

    const onInviteUserClick = async () => {
        Analytics.event('SEM::users_invites_at_least_one_team_member')

        setSubmitted(true)
        await form.trigger()

        if (form.formState.isValid) {
            setLoading(true)

            const values = form.getValues()
            const result = await SettingsTeamModule.inviteUser({
                ...values,
                email: normalizeEmail(values.email),
            })

            if (result) {
                Analytics.event('SEM::invitee_joined_ASM')
                generateToast({
                    type: 'success',
                    data: `Invite sent, once the invitee accepts the invite they will be added to the team page`,
                })
                closeDrawer()
            } else {
                Analytics.event('SEM::invitee_joined_ASM_failed')
                generateToast({ type: 'error', data: `Failed to invite user` })
            }

            setLoading(false)
        }
    }

    const closeDrawer = () => {
        form.reset()
        setDrawerState((prevState) => ({
            ...prevState,
            isDrawerOpen: false,
            drawerContentType: null,
        }))
    }

    return (
        <Wrapper>
            <Header>
                <Typography component="p" variant="xl-regular">
                    Invite new user
                </Typography>
            </Header>
            <Content>
                <FormProvider {...form}>
                    <InviteUserForm submitted={submitted} />
                </FormProvider>
            </Content>
            <Footer>
                <ActionButton
                    className="action-button"
                    text="Invite user"
                    loading={loading}
                    disabled={!form.formState.isValid || loading}
                    onClick={onInviteUserClick}
                />
            </Footer>
        </Wrapper>
    )
}

export default InviteUser
